import DateTimeDisplay from '@/components/DateTime';
import { PayoutTransactionClientData } from '@/types/Payouts';
import { Box, useTheme } from '@palmetto/palmetto-components';
import React from 'react';

import { PayoutTransactionLevel } from 'types';

interface TransactionDetailsViewProps {
  transaction: PayoutTransactionClientData;
}

const ListItemElement = ({ label, value }: { label: string; value: string }) => {
  return (
    <Box borderWidth="0 0 xs 0" borderColor="separator" flex="auto" direction="row" padding="xs 0">
      <Box flex="auto" as="h3" fontWeight="bold">
        {label}
      </Box>
      <Box>{value}</Box>
    </Box>
  );
};

export const TransactionDetailsView: React.FC<TransactionDetailsViewProps> = ({ transaction }) => {
  const { theme } = useTheme();
  const isPayeeLevel = transaction.transactionLevel === PayoutTransactionLevel.payee;
  const references = Object.entries(transaction.references ?? {});
  const createdByName =
    transaction.history && transaction.history.length > 0 && transaction.history[0].user?.name
      ? transaction.history[0].user.name
      : transaction.meta?.createdBy || '-';

  return (
    <Box background={theme === 'dark' ? 'grey-800' : 'primary'} padding="md">
      <Box display="flex" direction="column" gap="xs" alignItems="center" width="100%">
        <Box width="50%">
          <ListItemElement label="Created By" value={createdByName} />

          <Box borderWidth="0 0 xs 0" borderColor="separator" flex="auto" direction="row" padding="xs 0">
            <Box flex="auto" as="h3" fontWeight="bold">
              Created At
            </Box>
            <Box>
              {transaction.meta?.createdAt ? (
                <DateTimeDisplay displayLocalDateTime value={transaction.meta.createdAt} />
              ) : (
                '-'
              )}
            </Box>
          </Box>
          <ListItemElement label="Transaction ID" value={transaction.id || '-'} />
          {isPayeeLevel ? (
            references.length > 0 && (
              <Box borderWidth="0 0 xs 0" borderColor="separator" flex="auto" direction="row" padding="xs 0">
                <Box flex="auto" as="h3" fontWeight="bold">
                  References
                </Box>
                <Box direction="column" alignItems="flex-end">
                  {references.map(([key, value]) => (
                    <Box key={key}>{`${key}: ${value}`}</Box>
                  ))}
                </Box>
              </Box>
            )
          ) : (
            <>
              <ListItemElement label="Payee Type" value={transaction.payeeType || '-'} />

              {transaction.organizationPlanName && (
                <ListItemElement label="Organization Plan Name" value={transaction.organizationPlanName || '-'} />
              )}

              {transaction.isManual && <ListItemElement label="Manual Transaction" value="Yes" />}
            </>
          )}

          <Box borderWidth="0 0 xs 0" borderColor="separator" flex="auto" direction="column" padding="xs 0">
            <Box flex="auto" as="h3" fontWeight="bold" padding="0 0 xs 0">
              History
            </Box>
            {transaction.history && transaction.history.length > 0 ? (
              <Box direction="column" gap="xs">
                {transaction.history.map((historyItem, index) => (
                  <Box
                    key={index}
                    background={theme === 'dark' ? 'grey-700' : 'grey-50'}
                    padding="xs"
                    radius="sm"
                    margin="0 0 xs 0"
                    borderWidth="xs"
                    borderColor={theme === 'dark' ? 'grey-600' : 'grey-100'}
                  >
                    <Box direction="row" justifyContent="space-between" padding="0 0 xs 0">
                      <Box fontWeight="bold" fontSize="sm">
                        {historyItem.date && <DateTimeDisplay displayLocalDateTime value={historyItem.date} />}
                      </Box>
                      <Box fontSize="sm">{historyItem.user?.name || ''}</Box>
                    </Box>
                    <Box fontSize="sm">{historyItem.summary || '-'}</Box>
                  </Box>
                ))}
              </Box>
            ) : (
              <Box>No history available</Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
